import { useCallback, useEffect, useRef } from 'react';
import { ErrorsHandlerProps, ErrorType } from './ErrorsHandler.types';
import { isNewVersionAvailableError } from '../error-views/new-version-available-error/NewVersionAvailableError.utils';
import { logWarning } from 'src/_shared/logger';
import { observer } from 'mobx-react-lite';
import { isOfflineError } from '../error-views/offline-error/OfflineError.utils';
import { isThirdPartyNetworkError } from '../error-views/third-party-network-error/ThirdPartyNetworkError.utils';
import { extractErrorEventInfo } from '../error-views/ErrorViews.utils';
import { isNoAvailableSpaceError } from '../error-views/no-available-space-error/NoAvailableSpaceError.utils';

export const ErrorsHandler = observer(({ setError }: ErrorsHandlerProps) => {
  const isInit = useRef(false);
  const isNetworkAvailableRef = useRef(true);

  const errorHandler = useCallback((event: ErrorType['event']) => {
    logWarning(
      '[errorHandler] event',
      event,
      'extractErrorEventInfo: ',
      extractErrorEventInfo(event),
      'isNetworkAvailableRef: ',
      isNetworkAvailableRef.current,
    );

    const error: ErrorType = {
      event,
      isNetworkAvailable: isNetworkAvailableRef.current,
    };

    if (
      isOfflineError(error) ||
      isNoAvailableSpaceError(error) ||
      isNewVersionAvailableError(error) ||
      isThirdPartyNetworkError(error)
    ) {
      // do not block the user if it's a third-party network error
      if (!isThirdPartyNetworkError(error)) {
        setError(error);
      }

      event.preventDefault();
      event.stopPropagation();
    }
  }, []);

  const handleOnline = useCallback((event: ErrorType['event']) => {
    if (!isNetworkAvailableRef.current) {
      isNetworkAvailableRef.current = true;
      // remove last error if caused from offline event
      setError((prevError: ErrorType | undefined) => (prevError && isOfflineError(prevError) ? undefined : prevError));
    }
  }, []);

  const handleOffline = useCallback((event: ErrorType['event']) => {
    isNetworkAvailableRef.current = false;
  }, []);

  useEffect(() => {
    if (!isInit.current) {
      isInit.current = true;
      window.addEventListener('error', errorHandler, true);
      window.addEventListener('unhandledrejection', errorHandler);
      window.addEventListener('online', handleOnline);
      window.addEventListener('offline', handleOffline);
    }

    return () => {
      window.removeEventListener('error', errorHandler, true);
      window.removeEventListener('unhandledrejection', errorHandler);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return <></>;
});
